
import React, { useState, useEffect } from 'react'
import { Bell, X, AlertTriangle, Clock, CheckCircle } from 'lucide-react'
import { useContratos } from '../hooks/useContratos'

const NotificationBellContratos: React.FC = () => {
  const { contratos, getContratosExpirados } = useContratos()
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState<any[]>([])

  useEffect(() => {
    const generateNotifications = () => {
      const notifs: any[] = []
      
      // Contratos expirados
      const expirados = getContratosExpirados()
      expirados.forEach(contrato => {
        notifs.push({
          id: `exp-${contrato._id}`,
          type: 'expired',
          title: 'Contrato Expirado',
          message: `Contrato ${contrato.numero} - ${contrato.nomeCliente} expirou`,
          date: contrato.dataValidade,
          contratoId: contrato._id,
          priority: 'high'
        })
      })

      // Contratos próximos ao vencimento (7 dias)
      const proximosVencimento = contratos.filter(contrato => {
        const dataValidade = new Date(contrato.dataValidade)
        const hoje = new Date()
        const diasRestantes = Math.ceil((dataValidade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
        return diasRestantes <= 7 && diasRestantes > 0 && contrato.status !== 'aprovado' && contrato.status !== 'perdido'
      })

      proximosVencimento.forEach(contrato => {
        const diasRestantes = Math.ceil((new Date(contrato.dataValidade).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        notifs.push({
          id: `exp-soon-${contrato._id}`,
          type: 'expiring_soon',
          title: 'Contrato Próximo ao Vencimento',
          message: `Contrato ${contrato.numero} - ${contrato.nomeCliente} vence em ${diasRestantes} dia(s)`,
          date: contrato.dataValidade,
          contratoId: contrato._id,
          priority: 'medium'
        })
      })

      // Contratos aprovados recentemente (últimos 7 dias)
      const aprovadosRecentes = contratos.filter(contrato => {
        if (contrato.status !== 'aprovado') return false
        const dataAtualizacao = new Date(contrato.updatedAt)
        const hoje = new Date()
        const diasAtras = Math.ceil((hoje.getTime() - dataAtualizacao.getTime()) / (1000 * 60 * 60 * 24))
        return diasAtras <= 7
      })

      aprovadosRecentes.forEach(contrato => {
        notifs.push({
          id: `approved-${contrato._id}`,
          type: 'approved',
          title: 'Contrato Aprovado',
          message: `Contrato ${contrato.numero} - ${contrato.nomeCliente} foi aprovado`,
          date: contrato.updatedAt,
          contratoId: contrato._id,
          priority: 'low'
        })
      })

      // Ordenar por prioridade e data
      const priorityOrder = { high: 3, medium: 2, low: 1 }
      notifs.sort((a, b) => {
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority]
        }
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      })

      setNotifications(notifs)
    }

    generateNotifications()
  }, [contratos, getContratosExpirados])

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'expired':
        return <AlertTriangle size={16} className="text-red-500" />
      case 'expiring_soon':
        return <Clock size={16} className="text-orange-500" />
      case 'approved':
        return <CheckCircle size={16} className="text-green-500" />
      default:
        return <Bell size={16} className="text-blue-500" />
    }
  }

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'expired':
        return 'border-l-red-500 bg-red-50'
      case 'expiring_soon':
        return 'border-l-orange-500 bg-orange-50'
      case 'approved':
        return 'border-l-green-500 bg-green-50'
      default:
        return 'border-l-blue-500 bg-blue-50'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const unreadCount = notifications.length

  return (
    <div className="relative">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showNotifications && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-50">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Notificações de Contratos</h3>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                <p>Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-l-4 ${getNotificationColor(notification.type)} hover:bg-gray-50 cursor-pointer`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900">
                          {notification.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatDate(notification.date)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t bg-gray-50">
              <button
                onClick={() => setNotifications([])}
                className="w-full text-sm text-gray-600 hover:text-gray-800"
              >
                Marcar todas como lidas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBellContratos
